/**
 * Shell compartilhado do painel administrativo — tipografia Bilíngua (19/24).
 */
import styled from "styled-components";

import { pageRoot } from "./pageRoot";
import { md3CardRadius, md3ButtonRadius } from "../shared/styles/md3Components";
import {
  actionListSubtitle,
  actionListTitle,
  fontSizes,
  lineHeights,
  md3BodyLarge,
  md3LabelLarge,
  sectionTitle,
} from "../shared/styles/typography";

export const PageWrapper = styled.div`
  ${pageRoot}
  background: ${({ theme }) => theme.colors.layoutBg};
  transition: background 0.25s ease;
`;

export const DashboardMain = styled.main`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.contentStandardPx}px;
  margin: 1.6rem auto 4rem;
  padding: 0 max(1.2rem, env(safe-area-inset-left)) 0
    max(1.2rem, env(safe-area-inset-right));
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (min-width: 640px) {
    margin: 2.4rem auto 4.8rem;
    gap: 2.4rem;
  }

  @media (min-width: 900px) {
    margin: 3.2rem auto 5rem;
    padding: 0;
  }
`;

export const DashboardSection = styled.section`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => md3CardRadius(theme)}px;
  box-shadow: ${({ theme }) => theme.shadows.md3Level1};
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  transition: background 0.25s ease, border-color 0.25s ease;

  @media (min-width: 640px) {
    padding: 2rem;
    gap: 1.6rem;
  }
`;

export const ListHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  gap: 1rem;

  @media (min-width: 480px) {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
  }
`;

export const ModuleCardTitle = styled.h2`
  ${actionListTitle}
  color: ${({ theme }) => theme.colors.textTitle};
  margin: 0;
`;

export const ModuleCard = styled.div`
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border-radius: ${({ theme }) => md3CardRadius(theme)}px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  padding: 2rem;
`;

export const ModuleCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

export const ModuleCardDesc = styled.p`
  ${md3BodyLarge}
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

/** Botão primário admin — visual Md3 filled */
export const AddButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  min-height: 48px;
  padding: 0 1.8rem;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.titleOnPrimary};
  font-family: ${({ theme }) => theme.font.familyHeading};
  font-size: ${fontSizes.base}px;
  line-height: ${lineHeights.base}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: none;
  border-radius: ${({ theme }) => md3ButtonRadius(theme)}px;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: ${({ theme }) => theme.shadows.md3Level1};
  transition: opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-1px);
    box-shadow: ${({ theme }) => theme.shadows.md3Level2};
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media (max-width: 479px) {
    width: 100%;
  }
`;

export const Container = styled.div`
  ${pageRoot}
  background: ${({ theme }) => theme.colors.layoutBg};
  transition: background 0.25s ease;
`;

export const Main = styled.main`
  box-sizing: border-box;
  width: 100%;
  max-width: 760px;
  margin: 1.6rem auto 4rem;
  padding: 1.6rem;
  flex: 1;
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => md3CardRadius(theme)}px;
  box-shadow: ${({ theme }) => theme.shadows.md3Level1};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: background 0.25s ease, border-color 0.25s ease;

  @media (min-width: 640px) {
    margin: 2.4rem auto 5rem;
    padding: 2.4rem;
  }

  @media (min-width: 900px) {
    margin: 3.2rem auto 5rem;
    padding: 3.2rem;
  }

  fieldset {
    border: none;
    padding: 0;
    margin-bottom: 2.4rem;

    legend {
      ${sectionTitle}
      margin-bottom: 2rem;
      padding-bottom: 0.8rem;
      border-bottom: 1px solid ${({ theme }) => theme.colors.border};
      width: 100%;
    }
  }

  footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1.6rem;
    padding-top: 1.6rem;
    border-top: 1px solid ${({ theme }) => theme.colors.border};
    margin-top: 2.4rem;

    p {
      display: flex;
      align-items: center;
      gap: 1rem;
      ${actionListSubtitle}
      margin: 0;
    }

    button[type="submit"],
    .ant-btn {
      min-height: 48px;
      padding: 0 3.2rem;
      border-radius: ${({ theme }) => md3ButtonRadius(theme)}px;
      font-size: ${fontSizes.base}px;
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }
  }
`;

/** Área de conteúdo com tabela (listagens admin) */
export const AdminContentCard = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.contentStandardPx}px;
  margin: 0 auto 4rem;
  padding: 1.6rem;
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => md3CardRadius(theme)}px;
  box-shadow: ${({ theme }) => theme.shadows.md3Level1};
  border: 1px solid ${({ theme }) => theme.colors.border};

  @media (min-width: 640px) {
    padding: 2.4rem;
    margin-bottom: 5rem;
  }
`;

export const AdminSearchBar = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.contentStandardPx}px;
  margin: -1.2rem auto 0;
  padding: 1.4rem 1.6rem;
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => md3CardRadius(theme)}px;
  box-shadow: ${({ theme }) => theme.shadows.md3Level2};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  gap: 1.2rem;
  position: relative;
  z-index: 10;

  @media (min-width: 640px) {
    padding: 1.6rem 2rem;
  }
`;

export const AdminHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1.6rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  ${actionListTitle}
  color: ${({ theme }) => theme.colors.textTitle};
`;

export const StatusBadge = styled.span<{ $blocked?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  ${md3LabelLarge}
  background: ${({ $blocked, theme }) =>
    $blocked
      ? `color-mix(in srgb, ${theme.colors.danger} 14%, transparent)`
      : `color-mix(in srgb, ${theme.colors.success} 14%, transparent)`};
  color: ${({ $blocked, theme }) =>
    $blocked ? theme.colors.danger : theme.colors.success};
`;
